import {
  SlidersOutlined,
  PlusCircleOutlined,
  UploadOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import {
  Button,
  Empty,
  Input,
  InputNumber,
  message,
  Pagination,
  Popover,
  Select,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import queryString from 'query-string'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { useLocation, withRouter } from 'react-router-dom'
import AccessControlAction from '../../../components/AccessControlAction'
import AppPageHeader from '../../../components/AppPageHeader'
import { EmptyText, TextWrap } from '../../../components/Common/CellText'
import { StatusTag } from '../../../components/Common/StatusTag'
import TableComponent from '../../../components/Common/TableComponent'
import SimpleBreadCrumb from '../../../components/SimpleBreadCrumb/SimpleBreadCrumb'
import UnreadText from '../../../components/UnreadText/UnreadText'
import { systemName } from '../../../config'
import {
  ACL_ACTION_TYPE,
  DATE_FORMAT_DEFAULT,
  DOCUMENT_TYPE,
  TYPE_STATUS,
} from '../../../constants'
import DocumentsLayout from '../../../layouts/DocumentsLayout'
import utils from '../../../utils'
import AdvanceFilterBlock from './AdvanceFilterBlock'
import {
  FilterButtonWrapper,
  TableFooterActionWrapper,
} from './InternalDocumentIncomingDocPageStyled'

const { Search } = Input

const InternalDocumentIncomingDocPage = props => {
  const {
    history,
    internalDocumentStore,
    loadingAnimationStore,
    authorityStore,
    connectedDocumentStore,
  } = props

  const {
    internalDocumentIncomingList,
    internalDocumentIncomingListTotalCount,
    queryParamsIncoming,
  } = internalDocumentStore

  const location = useLocation()
  const queryStringParse = queryString.parse(location.search)

  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false)
  const [countFilter, setCountFilter] = useState(0)
  const [exportLoading, setExportLoading] = useState(false)
  const [isRefreshPage, setIsRefreshPage] = useState(true)
  const [checkPagination, setCheckPagination] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      const pageFromState =
        history.location.state?.page || queryStringParse.page || 0
      const onlyUnread =
        history.location.state?.onlyUnread ||
        queryStringParse.only_unread ||
        false
      const size = history.location.state?.size || queryStringParse.size || 10

      await handleChangeParams({
        only_unread: onlyUnread === 'true' || onlyUnread === true,
        page: parseInt(pageFromState),
        size: parseInt(size),
      })

      if (onlyUnread === 'true' || onlyUnread === true) {
        await internalDocumentStore.setQueryParamsIncoming({
          only_unread: true,
        })
      }
    }

    fetchData()
  }, [])

  const NGAY_DEN_WIDTH = 100
  const NGAY_VAN_BAN_WIDTH = 100
  const SO_HIEU_WIDTH = 120
  const TRANG_THAI_WIDTH = 120
  const TRICH_YEU_WIDTH = 220
  const CO_QUAN_BAN_HANH_WIDTH = 200
  const LANH_DAO_WIDTH = 150
  const SCROLL_WIDTH = 1000
  const SO_DEN_WIDTH = 60

  const tableColumns = useMemo(
    () => [
      {
        title: 'Số đến',
        width: SO_DEN_WIDTH,
        render: record => (
          <UnreadText
            text={record.incoming_number}
            isUnread={checkUnread(record)}
            width={SO_DEN_WIDTH}
            isXLVB={record.isXlvb}
            isButPhe={record.document_status}
          />
        ),
      },
      {
        title: 'Số hiệu',
        width: SO_HIEU_WIDTH,
        render: record => (
          <UnreadText
            text={record.document_number}
            isUnread={checkUnread(record)}
            width={SO_HIEU_WIDTH}
            isXLVB={record.isXlvb}
            isButPhe={record.document_status}
          />
        ),
      },
      {
        title: 'Ngày đến',
        width: NGAY_DEN_WIDTH,
        render: record => (
          <UnreadText
            text={moment(record.incoming_date).format(DATE_FORMAT_DEFAULT)}
            isUnread={checkUnread(record)}
            width={NGAY_DEN_WIDTH}
            isXLVB={record.isXlvb}
            isButPhe={record.document_status}
          />
        ),
      },
      {
        title: 'Ngày ban hành',
        width: NGAY_VAN_BAN_WIDTH,
        render: record =>
          record.date_issued ? (
            <UnreadText
              text={moment(record.date_issued).format(DATE_FORMAT_DEFAULT)}
              isUnread={checkUnread(record)}
              width={NGAY_VAN_BAN_WIDTH}
              isXLVB={record.isXlvb}
              isButPhe={record.document_status}
            />
          ) : (
            <TextWrap>
              <EmptyText>-------------------------</EmptyText>
            </TextWrap>
          ),
      },
      {
        align: 'center',
        title: 'Trạng thái',
        width: TRANG_THAI_WIDTH,
        render: record => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {StatusTag(
              TYPE_STATUS.INTERNAL_DOCUMENT_INCOMING,
              record.document_status
            )}
          </div>
        ),
      },
      {
        title: 'Trích yếu',
        width: TRICH_YEU_WIDTH,
        render: record => (
          <UnreadText
            text={record.title}
            isUnread={checkUnread(record)}
            width={TRICH_YEU_WIDTH}
            isXLVB={record.isXlvb}
            isButPhe={record.document_status}
          />
        ),
      },
      {
        title: 'Cơ quan ban hành',
        width: CO_QUAN_BAN_HANH_WIDTH,
        render: record => (
          <UnreadText
            text={renderAuthorityIssuedName(record)}
            isUnread={checkUnread(record)}
            width={CO_QUAN_BAN_HANH_WIDTH}
            isXLVB={record.isXlvb}
            isButPhe={record.document_status}
          />
        ),
      },
      {
        title: 'Lãnh đạo xử lý',
        width: LANH_DAO_WIDTH,
        render: record => {
          const leaderText =
            record.handler ||
            record.leader ||
            record.leader_name ||
            record.other_leader
          return leaderText ? (
            <UnreadText
              text={utils.getNameInCapitalize(leaderText)}
              isUnread={checkUnread(record)}
              width={LANH_DAO_WIDTH}
              isXLVB={record.isXlvb}
              isButPhe={record.document_status}
            />
          ) : (
            <TextWrap>
              <EmptyText>-------------------------</EmptyText>
            </TextWrap>
          )
        },
      },
    ],
    []
  )

  const checkUnread = record => record.status_internal_incoming !== 'READ'

  const renderAuthorityIssuedName = (name, isText = false) => {
    if (name.authority_issued_name) {
      return name.authority_issued_name
    }
    if (name.other_authority_issued) {
      return isText ? (
        name.other_authority_issued
      ) : (
        <span>{name.other_authority_issued}</span>
      )
    }
    return ''
  }

  const exportDocument = useCallback(async () => {
    setExportLoading(true)
    const col = tableColumns.map(col => col.title)
    let rows = [col]
    try {
      const res = await internalDocumentStore.getForExportInternalDocumentIncomingListAll()
      if (res && res.data && res.data.data) {
        res.data.data.forEach(item => {
          rows.push([
            item.incoming_number,
            item.document_number,
            moment(item.incoming_date).format(DATE_FORMAT_DEFAULT),
            item.date_issued
              ? moment(item.date_issued).format(DATE_FORMAT_DEFAULT)
              : '-------------------------',
            StatusTag(
              TYPE_STATUS.INTERNAL_DOCUMENT_INCOMING,
              item.document_status,
              true
            ),
            item.title,
            renderAuthorityIssuedName(item, true),
            item.handler
              ? utils.getNameInCapitalize(item.handler)
              : '-------------------------',
          ])
        })
      }
      utils.exportExcel(
        rows,
        'VBNB - văn bản đến - ' + moment().format('YYYY-MM-DD hmms')
      )
    } catch (error) {
      console.log(error)
      message.error(error.vi || 'Đã có lỗi xảy ra!')
    } finally {
      setExportLoading(false)
    }
  }, [])

  const handleChangeParams = params => {
    const updatedParams = {
      ...queryParamsIncoming,
      ...params,
      page: params.page !== undefined ? params.page : 0,
    }
    internalDocumentStore.setQueryParamsIncoming(updatedParams)
    const queryStringStringify = queryString.stringify(
      {
        ...queryStringParse,
        ...params,
        page: updatedParams.page,
        size: updatedParams.size || 10,
      },
      { skipNull: true }
    )
    history.replace(
      `/internal-document/incoming-document?${queryStringStringify}`,
      {
        ...history.location.state,
        page: updatedParams.page,
        size: updatedParams.size || 10,
        onlyUnread: updatedParams.only_unread,
      }
    )
  }

  const handleChangeUnread = event => {
    const only_unread = event?.target?.checked
    handleChangeParams({ only_unread })
  }

  const handleQuickSearch = value => {
    const trimmed = value ? value.trim() : null
    handleChangeParams({
      title: trimmed,
      incoming_number: trimmed,
      document_number: trimmed,
    })
  }

  const onChangePagination = pageIndex => {
    handleChangeParams({ page: pageIndex - 1 })
  }

  const getInternalDocumentIncomingList = async () => {
    try {
      loadingAnimationStore.setTableLoading(true)
      await internalDocumentStore.getInternalDocumentIncomingList()
    } catch (error) {
      console.log(error)
      message.error(error?.vi || 'Đã có lỗi xảy ra!')
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }

  useEffect(() => {
    internalDocumentStore.setQueryParamsIncoming({
      ...queryParamsIncoming,
      ...queryStringParse,
      page: history.location.state?.page || queryStringParse.page || 0,
    })
      ; (async () => {
        try {
          loadingAnimationStore.setTableLoading(true)
          await setCheckPagination(false)
          authorityStore.setAuthorityIssuedsQueryParams({
            type: DOCUMENT_TYPE.INCOMING,
            key: null,
          })
          await Promise.all([
            authorityStore.getAuthorityIssueds(),
            internalDocumentStore.getInternalDocumentIncomingList(),
          ])
          setIsRefreshPage(false)
        } catch (err) {
          console.log(err)
          message.error(err.vi || 'Đã có lỗi xảy ra!')
        } finally {
          loadingAnimationStore.setTableLoading(false)
          setCheckPagination(true)
        }
      })()
    return () => {
      internalDocumentStore.clearInternalDocumentIncomingList()
      internalDocumentStore.clearQueryParamsIncoming()
    }
  }, [])

  useEffect(() => {
    if (isRefreshPage) return
    getInternalDocumentIncomingList()
  }, [queryParamsIncoming])

  useEffect(() => {
    let count = 0
    if (queryStringParse.title) count++
    if (queryStringParse.document_number) count++
    if (queryStringParse.from_date_issued && queryStringParse.to_date_issued)
      count++
    if (
      queryStringParse.from_incoming_date &&
      queryStringParse.to_incoming_date
    )
      count++
    if (queryStringParse.signer) count++
    if (queryStringParse.authority_name) count++
    setCountFilter(count)
  }, [location])

  return (
    <DocumentsLayout
      title={
        <>
          <AppPageHeader
            title="Danh sách văn bản"
            addon={
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* <Checkbox
                  checked={queryParamsIncoming.only_unread === true}
                  onChange={handleChangeUnread}
                  style={{ marginRight: 8 }}>
                  Chưa đọc
                </Checkbox> */}
                <Search
                  style={{
                    width: 300,
                  }}
                  size="large"
                  allowClear
                  onSearch={value => handleQuickSearch(value)}
                  defaultValue={queryStringParse.title}
                  maxLength={500}
                  placeholder={'Tìm kiếm theo Trích yếu / S...'}
                  className="custom-search-input"
                />

                <FilterButtonWrapper style={{ display: 'inline-block' }}>
                  <span
                    className={'count-filter'}
                    style={{ display: !countFilter && 'none' }}>
                    {countFilter}
                  </span>
                  <Popover
                    content={
                      <AdvanceFilterBlock
                        handleChangeParams={handleChangeParams}
                        onClose={() => setShowAdvanceFilter(false)}
                      />
                    }
                    trigger="click"
                    visible={showAdvanceFilter}
                    onVisibleChange={visible => setShowAdvanceFilter(visible)}
                    placement="bottomRight"
                    overlayClassName="custom-page-filter-dropdown-container">
                    <Button
                      size="large"
                      icon={<SlidersOutlined />}
                    />
                  </Popover>
                </FilterButtonWrapper>

                <Button
                  size="large"
                  loading={exportLoading}
                  onClick={exportDocument}
                  icon={<UploadOutlined />}>
                  Xuất dữ liệu
                </Button>

                <AccessControlAction
                  aclActionType={ACL_ACTION_TYPE.create__INCOMING}>
                  <Button
                    size="large"
                    icon={<PlusCircleOutlined />}
                    onClick={() => {
                      connectedDocumentStore.clearDataReceiveDocument()
                      history.push(
                        '/internal-document/incoming-document/create'
                      )
                    }}
                    type={'primary'}>
                    Tạo mới văn bản
                  </Button>
                </AccessControlAction>
              </div>
            }
          />
        </>
      }
      selectedKey="incoming-document"
      linkToIncomingDocument="/internal-document/incoming-document"
      linkToOutgoingDocument="/internal-document/outgoing-document"
      linkToSignDocument="/internal-document/sign-document"
      linkToConfigDocument="/internal-document/config-document/document-books">
      <Helmet>
        <title>
          Văn bản đến | {systemName} Quản lý VB {systemName}
        </title>
      </Helmet>

      <TableComponent
        onRow={record => ({
          onClick: () => {
            const currentPage = queryParamsIncoming.page || 0
            history.push(
              `/internal-document/incoming-document/view/${record.code}`,
              {
                from: '/internal-document/incoming-document',
                page: currentPage,
                size: queryParamsIncoming.size || 10,
                onlyUnread: queryParamsIncoming.only_unread,
              }
            )
          },
        })}
        rowKey={record => record.id}
        dataSource={internalDocumentIncomingList}
        columns={tableColumns}
        pagination={false}
        scroll={{ x: SCROLL_WIDTH }}
        locale={{
          emptyText: checkPagination ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={'Không có dữ liệu'}
            />
          ) : (
            <div style={{ opacity: 0.1, height: '150px' }} />
          ),
        }}
        loading={{
          tip: 'Đang tải văn bản...',
          spinning: loadingAnimationStore.tableLoading,
        }}
      />
      <TableFooterActionWrapper>
        {checkPagination ? (
          <div className="customPaginationWrapper">
            <div className="left-content-box">
              <span>Hiển thị</span>
              <Select
                value={queryParamsIncoming.size || 10}
                onChange={value => handleChangeParams({ size: value, page: 0 })}
                style={{ width: 110 }}
                className="custom-select">
                <Select.Option value={10}>10 / trang</Select.Option>
                <Select.Option value={20}>20 / trang</Select.Option>
                <Select.Option value={50}>50 / trang</Select.Option>
                <Select.Option value={100}>100 / trang</Select.Option>
              </Select>
              <span>Đi đến</span>
              <InputNumber
                min={1}
                max={Math.ceil(
                  internalDocumentIncomingListTotalCount /
                  (queryParamsIncoming.size || 10)
                )}
                value={(queryParamsIncoming.page || 0) + 1}
                onPressEnter={e => {
                  const value = e.target.value
                  if (value) {
                    onChangePagination(
                      Number(value),
                      queryParamsIncoming.size || 10
                    )
                  }
                }}
                onBlur={e => {
                  const value = e.target.value
                  if (value) {
                    onChangePagination(
                      Number(value),
                      queryParamsIncoming.size || 10
                    )
                  }
                }}
                className="custom-input-number"
              />
            </div>
            <Pagination
              onChange={onChangePagination}
              hideOnSinglePage={true}
              total={internalDocumentIncomingListTotalCount}
              pageSize={queryParamsIncoming.size || 10}
              current={(queryParamsIncoming.page || 0) + 1}
              showSizeChanger={false}
              showLessItems
              className="customPagination"
            />
          </div>
        ) : (
          ''
        )}
      </TableFooterActionWrapper>
      <style>{`
      `}</style>
    </DocumentsLayout>
  )
}

InternalDocumentIncomingDocPage.propTypes = {}

export default memo(
  withRouter(
    inject(
      'internalDocumentStore',
      'loadingAnimationStore',
      'authorityStore',
      'connectedDocumentStore',
      'selectUserStore'
    )(observer(InternalDocumentIncomingDocPage))
  )
)
